import { showImperativeToast } from '../providers/UIProvider';
import { libraryService } from '../services/libraryService';
import { diagnosticLog } from './diagnosticLogger';
import { getSourceHealthSnapshot, initHomeLibraryFileServerProbeGate } from '../services/localLibraryIpcGate';

const TOAST_MS = 10_000;

type RootSessionState = {
  failures: number;
  toasted: boolean;
};

const sessionByRoot = new Map<string, RootSessionState>();

let cachedSources: Array<{ id: string; path: string; label?: string }> | null = null;
let sourcesFetchStarted = false;

function ensureSourcesCached(): void {
  if (cachedSources || sourcesFetchStarted) return;
  sourcesFetchStarted = true;
  void libraryService.getSources()
    .then((resp) => {
      cachedSources = Array.isArray(resp?.sources) ? resp.sources : [];
    })
    .catch(() => {
      cachedSources = [];
    });
}

function normalizePath(p: string): string {
  return String(p || '').trim().replace(/\\/g, '/');
}

/** True when the error matches an unmounted-volume play failure pattern. */
export function isUnmountedVolumeFailure(errMsg: string): boolean {
  const raw = String(errMsg || '').trim();
  if (!raw) return false;
  if (/^File not found:\s*.+/i.test(raw)) return true;
  if (/Home library unavailable/i.test(raw)) return true;
  if (/File not reachable on any registered machine/i.test(raw)) return true;
  return false;
}

/** Pull a filesystem path from the error text, or fall back to the track path. */
export function extractFailurePath(errMsg: string, fallbackPath?: string): string | null {
  const raw = String(errMsg || '').trim();
  const m = raw.match(/^File not found:\s*(.+)$/i);
  if (m?.[1]) return m[1].trim();
  const fb = String(fallbackPath || '').trim();
  return fb || null;
}

/** Derive a stable volume root and user-facing label from a file path. */
export function extractVolumeRoot(filePath: string): { root: string; displayName: string } | null {
  const raw = String(filePath || '').trim();
  if (!raw) return null;

  const mac = raw.match(/^\/Volumes\/([^/]+)(?:\/|$)/i);
  if (mac) {
    const name = mac[1];
    return { root: `/Volumes/${name}/`, displayName: name };
  }

  const unc = raw.match(/^\\\\([^\\]+)\\([^\\]+)(?:\\|$)/);
  if (unc) {
    const server = unc[1];
    const share = unc[2];
    const root = `\\\\${server}\\${share}\\`;
    return { root, displayName: `${server} (${share})` };
  }

  const fwdUnc = normalizePath(raw).match(/^\/\/([^/]+)\/([^/]+)(?:\/|$)/);
  if (fwdUnc) {
    const server = fwdUnc[1];
    const share = fwdUnc[2];
    const root = `\\\\${server}\\${share}\\`;
    return { root, displayName: `${server} (${share})` };
  }

  const drive = raw.match(/^([A-Za-z]):[\\/]/);
  if (drive) {
    const letter = drive[1].toUpperCase();
    return { root: `${letter}:\\`, displayName: `${letter}:` };
  }

  return null;
}

function rootKey(root: string): string {
  return normalizePath(root).toLowerCase().replace(/\/+$/, '');
}

function sourceMatchesRoot(
  sourcePath: string,
  volumeRoot: string,
  filePath?: string,
): boolean {
  const src = normalizePath(sourcePath);
  const root = normalizePath(volumeRoot).replace(/\/+$/, '');
  if (!src || !root) return false;
  if (src === root || src.startsWith(`${root}/`)) return true;
  if (filePath) {
    const fp = normalizePath(filePath);
    if (fp.startsWith(`${src}/`) || fp === src) return true;
  }
  const srcRoot = extractVolumeRoot(sourcePath);
  return !!srcRoot && rootKey(srcRoot.root) === rootKey(volumeRoot);
}

function isSourceRootConfirmedOffline(volumeRoot: string, filePath?: string): boolean {
  initHomeLibraryFileServerProbeGate();
  const health = getSourceHealthSnapshot();
  const sources = cachedSources || [];
  for (const src of sources) {
    if (!src?.id || !src?.path) continue;
    if (!sourceMatchesRoot(src.path, volumeRoot, filePath)) continue;
    const h = health[src.id];
    if (h?.status === 'offline') return true;
  }
  return false;
}

function driveToast(displayName: string, definitive: boolean): string {
  const connection = definitive
    ? "It isn't connected"
    : 'It may not be connected';
  return `Maestro can't see your music drive '${displayName}'. ${connection} — open it in Finder (or File Explorer), then try again.`;
}

function getRootState(root: string): RootSessionState {
  const key = rootKey(root);
  let state = sessionByRoot.get(key);
  if (!state) {
    state = { failures: 0, toasted: false };
    sessionByRoot.set(key, state);
  }
  return state;
}

/** Reset unmounted-volume tracking after a successful local play on this path. */
export function recordLocalPlaySuccess(filePath?: string | null): void {
  if (!filePath) return;
  const volume = extractVolumeRoot(filePath);
  if (!volume) return;
  sessionByRoot.delete(rootKey(volume.root));
}

/**
 * Apply the unmounted-drive heuristic for local play failures.
 * Returns true when the default per-track toast should be suppressed.
 */
export function applyUnmountedDriveHeuristic(
  errMsg: string,
  filePath?: string | null,
): boolean {
  if (!isUnmountedVolumeFailure(errMsg)) return false;

  ensureSourcesCached();
  initHomeLibraryFileServerProbeGate();

  const path = extractFailurePath(errMsg, filePath || undefined);
  if (!path) return false;

  const volume = extractVolumeRoot(path);
  if (!volume) return false;

  const state = getRootState(volume.root);
  state.failures += 1;

  if (state.failures < 2) {
    return false;
  }

  if (state.toasted) {
    return true;
  }

  state.toasted = true;
  const definitive = isSourceRootConfirmedOffline(volume.root, path);
  showImperativeToast(driveToast(volume.displayName, definitive), TOAST_MS);
  console.log(
    `[LocalFile] source root unreachable heuristic fired root=${volume.root} failures=${state.failures}`,
  );
  diagnosticLog('info', 'playback', '[LocalFile] source root unreachable heuristic fired', {
    root: volume.root,
    failures: state.failures,
    definitive,
    display_name: volume.displayName,
  });
  return true;
}
